import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getNoticeById, updateNotice } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  Phone,
  Save,
  AlertCircle,
  Clock,
  Mail,
  FileText,
  Star,
  Trash2,
  Megaphone,
  Bell,
  AlertTriangle,
  Info,
  Calendar,
  Upload,
  HelpCircle,
} from "lucide-react";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";

interface NoticeFormData {
  title: string;
  description: string;
  category: string;
  priority: "low" | "medium" | "high";
  tags: string;
  images: string[];
  visibility: "PUBLIC" | "VERIFIED" | "SUBSCRIBER";
  isPinned: boolean;
  requiresApproval: boolean;
  expiryDate: string;
}

const NOTICE_CATEGORIES = [
  { value: "announcement", label: "Announcement", icon: Bell },
  { value: "warning", label: "Warning", icon: AlertTriangle },
  { value: "info", label: "Information", icon: Info },
  { value: "maintenance", label: "Maintenance", icon: null },
  { value: "event", label: "Event", icon: Calendar },
  { value: "general", label: "General", icon: null },
];

// Tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function EditNoticePage() {
  const { noticeId } = useParams<{ noticeId: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<NoticeFormData>({
    title: "",
    description: "",
    category: "general",
    priority: "medium",
    tags: "",
    images: [],
    visibility: "VERIFIED",
    isPinned: false,
    requiresApproval: false,
    expiryDate: "",
  });

  // Fetch notice data
  const {
    data: notice,
    isLoading,
    error,
  } = useQuery(getNoticeById, { noticeId: noticeId! });

  // Initialize form data when notice loads
  useEffect(() => {
    if (notice) {
      setFormData({
        title: notice.title,
        description: notice.description || "",
        category: notice.category || "general",
        priority: notice.metadata?.priority || "medium",
        tags: notice.tags.join(", "),
        images: notice.images || [],
        visibility: notice.visibility as "PUBLIC" | "VERIFIED" | "SUBSCRIBER",
        isPinned: notice.isPinned,
        requiresApproval: notice.requiresApproval,
        expiryDate: notice.expiryDate
          ? new Date(notice.expiryDate).toISOString().split("T")[0]
          : "",
      });
    }
  }, [notice]);

  const handleChange = (field: keyof NoticeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tagsArray = formData.tags
        ? formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
        : [];

      await updateNotice({
        noticeId: noticeId!,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: tagsArray,
        images: formData.images,
        visibility: formData.visibility,
        isPinned: formData.isPinned,
        requiresApproval: formData.requiresApproval,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        metadata: {
          priority: formData.priority,
        },
      });

      // Navigate back to notices
      handleGoBack();
    } catch (error) {
      console.error("Error updating notice:", error);
      setErrors({ submit: "Failed to update notice. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/notices`);
    } else {
      navigate("/notices");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to edit notices.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Notice...">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading notice...</p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (error || !notice) {
    return (
      <IndividualEditPageLayout title="Notice Not Found" icon={AlertCircle}>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Notice Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The notice you're looking for doesn't exist or you don't have
              permission to edit it.
            </p>
            <button
              onClick={() => navigate("/notices")}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors rounded-lg"
            >
              Back to Notice Board
            </button>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  // Check if user can edit this notice
  if (notice.authorId !== user.id) {
    return (
      <IndividualEditPageLayout
        title="Permission Denied"
        subtitle="You can only edit your own notices"
        icon={AlertCircle}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Permission Denied
            </h3>
            <p className="text-slate-600 mb-6">
              You don't have permission to modify this notice.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="notice-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Saving..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Information" hideOnMobile>
        <p className="text-sm text-gray-500">
          Ensure your notice details are accurate before saving. Changes will be
          reflected immediately.
        </p>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Notice"
      subtitle={`Manage notice: ${formData.title}`}
      icon={Megaphone}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="notice-form" onSubmit={handleSubmit}>
        {/* Full-width sections */}
        <div className="space-y-6 mb-6">
          {/* What are you posting? */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              What are you posting?
            </h3>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${errors.title
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
                }`}
              placeholder="e.g., Scheduled Maintenance Notice, Community Event"
              required
            />
            {errors.title && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Description
            </h3>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${errors.description
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
                }`}
              placeholder="Provide detailed content of the notice, instructions, contact information, important information..."
              required
            />
            {errors.description && (
              <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
          </div>
        </div>

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Category and Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  <option value="">Select a category</option>
                  {NOTICE_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    handleChange(
                      "priority",
                      e.target.value as "low" | "medium" | "high",
                    )
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>
            </div>

            {/* Images */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 mb-2">
                    Upload images to support your notice
                  </p>
                  <p className="text-sm text-gray-400">
                    PNG, JPG up to 10MB each
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <span>Tags</span>
                  <Tooltip content="Separate tags with commas">
                    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => handleChange("tags", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., maintenance, urgent, facility (separate with commas)"
              />
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
